<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: student.full_name,
                        href: meta.links.student_show,
                    },
                    {
                        name: $tc('messages.fouls', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex space-x-4 justify-end">
            <AppButton :href="meta.links.create" element="a">
                {{ $t('messages.create') }}
            </AppButton>
            <AppButton :href="meta.links.print" element="a">
                {{ $t('messages.print') }}
            </AppButton>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.subject', 1)" for="subject_id" />
                        <FormSelect
                            id="subject_id"
                            v-model="form.subject_id"
                            :options="options.subjects"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3" v-show="options.school_years.length > 1">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                        <FormSelect
                            required
                            id="school_year_id"
                            v-model="form.school_year_id"
                            :options="options.school_years"
                            class="mt-1 w-full"
                            :null-option="false"
                        />
                        <FormError :value="form.errors.school_year_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="fouls.length === 0" class="flex flex-col mt-5">
            <EmptyState picture />
        </div>
        <div v-else class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.subject', 1) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.date') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.type') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(foul, index) in fouls"
                                :key="foul.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ foul.subject_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>{{ foul.date }}</div>
                                    <div class="text-sm">{{ foul.turn_display }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div>
                                        {{ foul.type_display }}
                                    </div>
                                    <div>
                                        {{ foul.severity_display }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton action="show" :href="foul.links.show" />
                                    <InertiaSquareLinkButton
                                        element="a"
                                        action="print"
                                        :href="foul.links.print"
                                        :title="$t('messages.print')"
                                        target="_blank"
                                    />
                                    <InertiaSquareLinkButton
                                        action="edit"
                                        :href="foul.links.edit"
                                        v-if="foul.can.edit"
                                    />
                                    <InertiaSquareDeleteButton
                                        :href="foul.links.delete"
                                        v-if="foul.can.delete"
                                        :confirm-text="$t('messages.are_you_sure?')"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <div class="pt-5" v-if="fouls.length">
                <div class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div
                        class="bg-white overflow-hidden shadow rounded-lg"
                        v-for="counter in counters"
                        :key="counter.key"
                    >
                        <div class="px-4 py-5 sm:p-6">
                            <dl>
                                <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                                    {{ counter.type }} <span v-if="counter.severity">- {{ counter.severity }}</span>
                                </dt>
                                <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                                    {{ counter.count }}
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import EmptyState from '@/components/EmptyState.vue';
import AppButton from '@/components/AppButton.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        InertiaSquareDeleteButton,
        PanelPage,
        InertiaSquareLinkButton,
        FormLabel,
        FormError,
        FormSelect,
        EmptyState,
        AppButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'fouls', 'filters', 'student', 'options', 'counters'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                school_year_id: this.filters.school_year_id,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
